/**
 * tenantPath.ts — 법인 경로 함수(설계: docs/design/10_법인경로_전환설계.md §1·§2·§5).
 *
 * 화면·서비스는 컬렉션 이름을 직접 적지 않고 `tenantColPath(법인, "mixes", 사업?)` 로 실제 주소를 받는다.
 * 법인 › 사업 2단계(D-26, 2026-10-22): 사업별 자료는 `units/{사업}` 아래, 법인 공통 자료(급여·원가 장부)는 법인 바로 아래.
 *   - gyeongwon 사업별     → `tenants/gyeongwon/units/ascon/<col>` (처음부터 법인 경로)
 *   - gyeongwon 법인 공통  → `tenants/gyeongwon/<col>` (TENANT_SCOPE_COLLECTIONS)
 *   - joongang + 아직 루트 → `<col>` (지금 주소 그대로 — S1 은 모든 중앙 주소가 100% 같아야 함)
 *   - joongang + 이전 완료 → `tenants/joongang/units/rmc/<col>` 또는 법인 공통 `tenants/joongang/<col>`
 *                            (S6 묶음별 이전 시 JOONGANG_AT_ROOT 에서 빼면 됨)
 *   - 그룹 공통(§1 (a))    → 그대로 반환
 *
 * 연결표는 런타임 설정이 아니라 코드 상수(설계 §2). Functions 쪽(backend/functions/src)에도 같은 표를 둘 것.
 * 순수 상수·함수만. Firebase import 금지(domain 규칙).
 */
import { defaultBiz, tenantHasBiz, type BizKey, type TenantKey } from "./space";

export type { BizKey, TenantKey };

/** S1 단계: 모든 서비스의 기본 법인. 법인 전환(S4) 전까지 호출부는 이 값을 쓴다. */
export const DEFAULT_TENANT: TenantKey = "joongang";

// ── 법인 소속 컬렉션(§1 (b) + (c) 중 법인 소속 요청 문서) — 이전 묶음별 ─────────────

/** ① 영업 미러 — ERP SQL 단방향 미러(read-only). 새 경로로 deep sync 한 번 → 읽기 전환. */
export const BUNDLE_SALES = [
  "sales_invoices",
  "sales_receipts",
  "sales_customers",
  "sales_sites",
  "sales_directory",    // 거래처·현장 검색 목록(여신·미수 없는 사본, directory.view) — 부서별-권한 §5-A
  "sales_reps",
  "sales_balances",
  "sales_allocations",
  "sales_sync_status",
  "sales_targets",      // 웹 참조 없음 — 폐기 후보(S0)
  "sales_sync_ops",     // (c) 요청 문서
  "sales_customer_meta", // ⚠️ 미러 아님(자체 입력: 거래처 부가정보·코멘트) — 이전 시 쓰기 정지 필요
] as const;

/** ② 출하 미러 + 알림 트리거 — RQC SQL 미러. */
export const BUNDLE_SHIPMENT = [
  "daily_shipments",
  "daily_shipment",          // 단수 = RQC raw (복수와 별개, 둘 다 유지)
  "daily_shipment_details",
  "shipment_tickets",
  "shipment_monthly",
  "rqc_drift_log",
  "month_backfill_log",
  "shipment_notify_state",   // CF 전용(rules 블록 없음)
  "rqc_requests",            // (c)
  "rqc_drift_check_requests", // (c)
] as const;

/** ③ 자재·배합·단가 — 자체 데이터(이전 시 짧은 쓰기 정지). */
export const BUNDLE_MATERIAL = [
  "mixes",
  "mix_import_snapshots",
  "material_categories",
  "raw_materials",
  "material_inbound",
  "material_outbound",
  "material_usage",  // 웹 참조 없음 — 폐기 후보(S0)
  "ledger_carry",    // 웹 참조 없음 — 폐기 후보(S0)
  "material_mappings",
  "supplier_prices",
  "market_prices",
] as const;

/** ④ 견적·원가 — 견적이 배합·단가·원가를 함께 읽으므로 ③과 붙여서 이전 권장. 원가 원장 rules 경로 매개변수화 필요. */
export const BUNDLE_QUOTE_COST = [
  "quotes",
  "cost_settings",
  "cost_order",
  "cost_ledger_entries",
  "cost_ledger_config",
  "cost_ledger_state",
  "cost_ledger_imports",
  "cost_ledger_months",
  "quote_cost_requests", // (c)
] as const;

/** ⑤ 급여 — 3단계(직원 명부 people/employments 분리, D-11)와 함께 검토. */
export const BUNDLE_PAYROLL = [
  "employees",
  "payslips",
  "payslip_view_log", // 웹 참조 없음
  "payslip_send_ops", // (c)
] as const;

/** 묶음 미정 — 현장·차량 마스터(자체 데이터). 출하(②) 또는 ③과 함께 옮길지 결정 필요. */
export const BUNDLE_SITE_VEHICLE = [
  "site_meta",
  "vehicles",
  "site_aliases",   // 옛 컬렉션(site_meta 로 대체) — 폐기 후보(S0)
  "site_locations", // 〃
] as const;

/**
 * 경원 OKIS 미러 — 중계 PC 수집기(tools/collector)가 쓰고 화면은 읽기만(2026-10-22 rules 배포, D-25 B안).
 * 경원 아스콘 전용: tenants/gyeongwon/units/ascon/<col>. 중앙 루트에는 없다.
 */
export const BUNDLE_OKIS_MIRROR = [
  "ascon_orders",    // 수주 R_SUJU
  "ascon_tickets",   // 계근 SELL
  "sync_status",     // 수집기 생존 신호(okis)
  "okis_change_log", // 바뀐·사라진 기록(D-24), 관리자만 읽기
] as const;

export const TENANT_BUNDLES = {
  sales: BUNDLE_SALES,
  shipment: BUNDLE_SHIPMENT,
  material: BUNDLE_MATERIAL,
  quote_cost: BUNDLE_QUOTE_COST,
  payroll: BUNDLE_PAYROLL,
  site_vehicle: BUNDLE_SITE_VEHICLE,
  okis_mirror: BUNDLE_OKIS_MIRROR,
} as const;

export type TenantCollection =
  | (typeof BUNDLE_SALES)[number]
  | (typeof BUNDLE_SHIPMENT)[number]
  | (typeof BUNDLE_MATERIAL)[number]
  | (typeof BUNDLE_QUOTE_COST)[number]
  | (typeof BUNDLE_PAYROLL)[number]
  | (typeof BUNDLE_SITE_VEHICLE)[number]
  | (typeof BUNDLE_OKIS_MIRROR)[number];

/** 런타임 화이트리스트 — 법인 소속 컬렉션 전체. */
export const TENANT_COLLECTIONS: readonly TenantCollection[] = [
  ...BUNDLE_SALES,
  ...BUNDLE_SHIPMENT,
  ...BUNDLE_MATERIAL,
  ...BUNDLE_QUOTE_COST,
  ...BUNDLE_PAYROLL,
  ...BUNDLE_SITE_VEHICLE,
  ...BUNDLE_OKIS_MIRROR,
];

/**
 * 법인 공통(사업 단계 없음) 컬렉션 — D-26: 급여·직원(법인 단위 신고)과 원가 장부(법인 회계).
 * 그 밖의 법인 소속 컬렉션은 사업별(`units/{사업}`). 원가 설정(cost_settings·cost_order)은 견적이 쓰는 사업 단가 기준이라 사업별.
 */
export const TENANT_SCOPE_COLLECTIONS: readonly TenantCollection[] = [
  ...BUNDLE_PAYROLL,
  "cost_ledger_entries",
  "cost_ledger_config",
  "cost_ledger_state",
  "cost_ledger_imports",
  "cost_ledger_months",
];
const TENANT_SCOPE_SET: ReadonlySet<string> = new Set<string>(TENANT_SCOPE_COLLECTIONS);

export function isTenantScopeCollection(col: string): boolean {
  return TENANT_SCOPE_SET.has(col);
}

/** 법인 아래 경로 접두어 — 법인 공통이면 `tenants/{t}`, 사업별이면 `tenants/{t}/units/{biz}` */
function tenantBase(tenant: TenantKey, biz: BizKey | null | undefined, tenantScope: boolean): string {
  if (tenantScope) return `tenants/${tenant}`;
  const b = tenantHasBiz(tenant, biz) ? biz : defaultBiz(tenant);
  return `tenants/${tenant}/units/${b}`;
}

/**
 * 지금 루트에 있는 중앙 자료는 레미콘(rmc) 것이다. 루트 예외(JOONGANG_AT_ROOT)는 중앙의 법인 공통 자료와
 * 레미콘에만 적용하고, 새 사업(석산)은 처음부터 `tenants/joongang/units/aggregate/…` 를 쓴다(레미콘 자료와 섞임 방지).
 */
function isLegacyRootUnit(tenant: TenantKey, biz: BizKey | null | undefined, tenantScope: boolean): boolean {
  if (tenantScope) return true;
  return (tenantHasBiz(tenant, biz) ? biz : defaultBiz(tenant)) === "rmc";
}

// ── 그룹 공통(§1 (a)) — 루트 유지 ─────────────────────────────────────────────

export const GROUP_COLLECTIONS = [
  "users",
  "roles",
  "role_default_layouts",
  "system_settings", // 키별로 그룹/법인이 갈림 → tenantSettingsDocPath 사용
  "audit_log",
  "perm_requests",
  "signup_requests",
  "pending_signups",
  "verification_codes",
  "password_reset_tokens",
  "mail",
  "mail_templates",
  "payslip_tokens",
  "relay_ops",
  "admin_ops",
  "workday_fetch_requests",
  "payslip_verify_requests",
  // 날씨는 Q4(법인별 여부) 미결 — 결정 전까지 그룹으로 둔다.
  "weather_current",
  "weather_forecast",
  "weather_sync_status",
  "weather_sync_requests",
] as const;
export type GroupCollection = (typeof GROUP_COLLECTIONS)[number];

/**
 * 중앙에서 아직 루트에 남아 있는 컬렉션. 지금(S1)은 법인 소속 전부.
 * S6 묶음 이전이 끝날 때마다 해당 묶음을 여기서 빼고 hosting 재배포 → 중앙 주소가 tenants/joongang/… 로 바뀐다.
 */
export const JOONGANG_AT_ROOT: ReadonlySet<string> = new Set<string>(TENANT_COLLECTIONS);

const TENANT_SET: ReadonlySet<string> = new Set<string>(TENANT_COLLECTIONS);
const GROUP_SET: ReadonlySet<string> = new Set<string>(GROUP_COLLECTIONS);

function devWarn(msg: string): void {
  if (import.meta.env?.DEV) console.warn(`[tenantPath] ${msg}`);
}

export function isTenantCollection(col: string): col is TenantCollection {
  return TENANT_SET.has(col);
}

/** 컬렉션의 실제 Firestore 경로(collection(db, path) 에 그대로 넣는다). biz 생략 = 그 법인의 기본 사업. */
export function tenantColPath(
  tenant: TenantKey,
  col: TenantCollection | GroupCollection,
  biz?: BizKey | null,
): string {
  if (GROUP_SET.has(col)) {
    devWarn(`그룹 공통 컬렉션 "${col}" 은 법인 경로를 거치지 않습니다 — 그대로 반환.`);
    return col;
  }
  if (!TENANT_SET.has(col)) {
    // 타입상 불가능. 런타임 방어: 미등록 컬렉션은 법인 경로로 격리(경원 화면에 중앙 데이터 노출 방지).
    devWarn(`미등록 컬렉션 "${col}" — TENANT_BUNDLES 또는 GROUP_COLLECTIONS 에 등록하세요.`);
  }
  const scope = TENANT_SCOPE_SET.has(col);
  if (tenant === "joongang" && JOONGANG_AT_ROOT.has(col) && isLegacyRootUnit(tenant, biz, scope)) return col;
  return `${tenantBase(tenant, biz, scope)}/${col}`;
}

/** 서비스용 축약 — 법인 생략 시 DEFAULT_TENANT, 사업 생략 시 그 법인의 기본 사업. */
export function tcol(col: TenantCollection, tenant: TenantKey = DEFAULT_TENANT, biz?: BizKey | null): string {
  return tenantColPath(tenant, col, biz);
}

// ── system_settings 키 — 그룹 키는 루트, 법인 키는 tenants/{t}/settings/{key} ──────
//
// 12_통합관리_계정기기설계 §4-3·§9 (2026-10-23 D 묶음):
//   - company_info  : 그룹 → **법인 공통**(tenants/{t}/settings/company_info). 루트 system_settings/company_info 는
//                     로그인 화면·사이드바 로고·그룹명(브랜드)으로 계속 읽는다(키 이름 유지 — §9-1 ③ 추천안, BRAND_SETTINGS_DOC_PATH).
//   - notify_policy : ★ 사업부별 조직 알림 on/off(tenants/{t}/units/{biz}/settings/notify_policy). 처음부터 법인 경로.
//   - workday_config·notification_settings·push_notify_copy : 그룹(통합관리) 그대로.

/** 그룹 공통 설정 키(§1 (a)). workday_config 는 두 법인 통합 휴무일(12 §0 사용자 결정). */
export const GROUP_SETTING_KEYS = [
  "notification_settings",
  "push_notify_copy",
  "workday_config",
  "rank_list", // 통합 직급 목록(두 법인 공통, 2026-10-26) — domain/rank.ts
] as const;

/** 법인 설정 키(§1 (b) system_settings 법인 키). 법인 공통 키는 TENANT_SCOPE_SETTING_KEYS 에도 넣는다. */
export const TENANT_SETTING_KEYS = [
  "company_info",
  "dispatch",
  "yongcha_config",
  "ship_sim_config",
  "payslip_settings",
  "material_wavg_exclusions",
  "line_order",
  "line_capacity",
  "quote.settings",
  "plant_info",
  "factory_camera",
  "category_order",
  "sales_ar_exclusions",
  "sales_ar_policy",
  "sales_reps_config",
  "rqc",
  "notify_policy",
] as const;
export type TenantSettingKey = (typeof TENANT_SETTING_KEYS)[number];

/** 법인 공통 설정 키(사업 단계 없음, D-26) — 급여 발송 설정·회사정보. 나머지 법인 키는 사업별. */
export const TENANT_SCOPE_SETTING_KEYS = ["payslip_settings", "company_info"] as const;
const TENANT_SCOPE_SETTING_SET: ReadonlySet<string> = new Set<string>(TENANT_SCOPE_SETTING_KEYS);

const GROUP_SETTING_SET: ReadonlySet<string> = new Set<string>(GROUP_SETTING_KEYS);
const TENANT_SETTING_SET: ReadonlySet<string> = new Set<string>(TENANT_SETTING_KEYS);

/** 처음부터 법인 경로를 쓰는 키 — 중앙이라도 루트(system_settings)로 보내지 않는다. */
const TENANT_PATH_FROM_START: ReadonlySet<string> = new Set<string>([
  "company_info",  // §9-1 ③ — 루트 문서는 브랜드로 남고, 법인 문서가 없으면 화면이 루트 값으로 대체 읽기(복사 전 무중단)
  "notify_policy", // 새 키
]);

/**
 * 중앙 법인 설정 중 아직 루트(system_settings)에 있는 키. company_info·notify_policy 를 뺀 전부.
 * S6 이전(§9-2, copy_settings_to_unit.js) 때 키 묶음별로 여기서 빼고 hosting 재배포.
 */
export const JOONGANG_SETTINGS_AT_ROOT: ReadonlySet<string> = new Set<string>(
  TENANT_SETTING_KEYS.filter((k) => !TENANT_PATH_FROM_START.has(k)),
);

/** 루트 브랜드 문서(로고·그룹명) — 로그인 화면·사이드바. 공개 읽기(firestore.rules system_settings/company_info) */
export const BRAND_SETTINGS_DOC_PATH = "system_settings/company_info";

/** 설정 키가 법인 공통(사업부 칩과 무관)인가 */
export function isTenantScopeSettingKey(key: string): boolean {
  return TENANT_SCOPE_SETTING_SET.has(key);
}

/** 설정 문서의 실제 경로(doc(db, path) 에 그대로 넣는다). 사업별 키는 `tenants/{t}/units/{biz}/settings/{key}`. */
export function tenantSettingsDocPath(
  key: string,
  tenant: TenantKey = DEFAULT_TENANT,
  biz?: BizKey | null,
): string {
  if (GROUP_SETTING_SET.has(key)) return `system_settings/${key}`;
  if (!TENANT_SETTING_SET.has(key)) {
    devWarn(`미등록 설정 키 "${key}" — GROUP_SETTING_KEYS 또는 TENANT_SETTING_KEYS 에 등록하세요.`);
  }
  const scope = TENANT_SCOPE_SETTING_SET.has(key);
  if (tenant === "joongang" && JOONGANG_SETTINGS_AT_ROOT.has(key) && isLegacyRootUnit(tenant, biz, scope)) {
    return `system_settings/${key}`;
  }
  return `${tenantBase(tenant, biz, scope)}/settings/${key}`;
}

/**
 * 설정 저장 위치 — 어느 법인·사업부의 설정인가(12 §9-3). 화면은 `useSettingsAt()`(hooks/useTenantPath.ts)으로 받아
 * repository 에 그대로 넘긴다. biz 생략 = 그 법인의 기본 사업. 법인 공통 키는 biz 를 무시한다.
 */
export interface SettingsAt {
  tenant: TenantKey;
  biz?: BizKey | null;
}

/** 옛 동작(중앙 레미콘) — 법인 공간 밖(로그인 화면 등)에서 쓰는 기본값 */
export const DEFAULT_SETTINGS_AT: SettingsAt = { tenant: DEFAULT_TENANT, biz: null };

/** SettingsAt 기준 설정 문서 경로 */
export function settingsDocPath(key: string, at: SettingsAt): string {
  return tenantSettingsDocPath(key, at.tenant, at.biz);
}

/** 구독 캐시·상태 키 — 같은 문서를 가리키면 같은 값(법인 공통 키는 사업부가 달라도 같음) */
export function settingsAtKey(key: string, at: SettingsAt): string {
  return settingsDocPath(key, at);
}
